import { memo, useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { getTooltipStyle, LABEL_STYLE } from '../shared/constants';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LabelList,
} from 'recharts';
import { ChartWrapper } from '../shared/ChartWrapper';
import { NoChartData } from '../shared/NoChartData';
import { useInnovationData } from './useInnovationData';
import { X, Search, ChevronLeft, ChevronRight } from 'lucide-react';

/* ── Custom SVG Sankey: Readiness Level → Use Level ────────────────────── */

const READINESS_COLOR = '#8b5cf6';
const USE_COLOR = '#22c55e';
const NODE_W = 14;
const NODE_PAD = 6;
const MIN_NODE_H = 4;
const LABEL_LEFT = 80;
const LABEL_RIGHT = 70;
const HEADER_H = 30;
const BOTTOM_PAD = 12;
const ALL_LEVELS = [9, 8, 7, 6, 5, 4, 3, 2, 1]; // descending — 9 at top

function bezierPath(x1, y1, h1, x2, y2, h2) {
  const mx = (x1 + x2) / 2;
  return `M${x1},${y1} C${mx},${y1} ${mx},${y2} ${x2},${y2} L${x2},${y2 + h2} C${mx},${y2 + h2} ${mx},${y1 + h1} ${x1},${y1 + h1} Z`;
}

function DevUseSankey({ flows }) {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(600);
  const [hovered, setHovered] = useState(null); // { type: 'link'|'node', key: string }
  const [tip, setTip] = useState('');
  const [tipPos, setTipPos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (!containerRef.current) return;
    const obs = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect?.width;
      if (w && w > 100) setWidth(w);
    });
    obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, []);

  const layout = useMemo(() => {
    // Aggregate totals per level — include all 9 levels
    const readinessTotals = {};
    const useTotals = {};
    ALL_LEVELS.forEach(l => { readinessTotals[l] = 0; useTotals[l] = 0; });
    flows.forEach(f => {
      readinessTotals[f.readiness] = (readinessTotals[f.readiness] || 0) + f.count;
      useTotals[f.use] = (useTotals[f.use] || 0) + f.count;
    });

    const totalFlow = flows.reduce((s, f) => s + f.count, 0);

    // Slot-based layout: 9 equal slots, nodes centered within each
    const slotH = 50;
    const chartH = HEADER_H + ALL_LEVELS.length * slotH + BOTTOM_PAD;
    const maxTotal = Math.max(...ALL_LEVELS.map(l => readinessTotals[l]), ...ALL_LEVELS.map(l => useTotals[l]), 1);

    const positionNodes = (totals, x) => {
      const nodes = {};
      ALL_LEVELS.forEach((l, i) => {
        const centerY = HEADER_H + i * slotH + slotH / 2;
        const h = totals[l] > 0
          ? Math.max((totals[l] / maxTotal) * (slotH - NODE_PAD), MIN_NODE_H)
          : MIN_NODE_H;
        nodes[l] = { x, y: centerY - h / 2, h, total: totals[l] };
      });
      return nodes;
    };

    const leftX = LABEL_LEFT;
    const rightX = width - LABEL_RIGHT - NODE_W;
    const rNodes = positionNodes(readinessTotals, leftX);
    const uNodes = positionNodes(useTotals, rightX);

    // Compute link positions
    const rOffsets = {};
    const uOffsets = {};
    ALL_LEVELS.forEach(l => { rOffsets[l] = 0; uOffsets[l] = 0; });

    const sortedFlows = [...flows].sort((a, b) => b.readiness - a.readiness || b.use - a.use);

    const links = sortedFlows.map(f => {
      const rNode = rNodes[f.readiness];
      const uNode = uNodes[f.use];
      const rH = rNode.total > 0 ? (f.count / rNode.total) * rNode.h : MIN_NODE_H;
      const uH = uNode.total > 0 ? (f.count / uNode.total) * uNode.h : MIN_NODE_H;
      const link = {
        readiness: f.readiness,
        use: f.use,
        count: f.count,
        x1: leftX + NODE_W,
        y1: rNode.y + rOffsets[f.readiness],
        h1: rH,
        x2: rightX,
        y2: uNode.y + uOffsets[f.use],
        h2: uH,
        key: `${f.readiness}-${f.use}`,
      };
      rOffsets[f.readiness] += rH;
      uOffsets[f.use] += uH;
      return link;
    });

    return { rNodes, uNodes, links, chartH, totalFlow, leftX, rightX };
  }, [flows, width]);

  const { rNodes, uNodes, links, chartH, leftX, rightX } = layout;

  const isLinkHighlighted = (link) => {
    if (!hovered) return false;
    if (hovered.type === 'link') return hovered.key === link.key;
    if (hovered.type === 'rNode') return link.readiness === hovered.level;
    if (hovered.type === 'uNode') return link.use === hovered.level;
    return false;
  };

  const isAnythingHovered = hovered !== null;

  const handleMouseMove = (e) => setTipPos({ x: e.clientX, y: e.clientY });

  return (
    <div ref={containerRef} className="relative w-full">
      <svg width={width} height={chartH}>
        <defs>
          <linearGradient id="sankey-grad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor={READINESS_COLOR} stopOpacity={0.35} />
            <stop offset="100%" stopColor={USE_COLOR} stopOpacity={0.35} />
          </linearGradient>
          <linearGradient id="sankey-grad-hi" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor={READINESS_COLOR} stopOpacity={0.7} />
            <stop offset="100%" stopColor={USE_COLOR} stopOpacity={0.7} />
          </linearGradient>
        </defs>

        {/* Column headers */}
        <text x={leftX + NODE_W / 2} y={16} textAnchor="middle" fontSize={12} fontWeight={700} fill="#6b7280">
          Readiness Level
        </text>
        <text x={rightX + NODE_W / 2} y={16} textAnchor="middle" fontSize={12} fontWeight={700} fill="#6b7280">
          Use Level
        </text>

        {/* Links */}
        {links.map(link => {
          const hi = isLinkHighlighted(link);
          const dimmed = isAnythingHovered && !hi;
          return (
            <path
              key={link.key}
              d={bezierPath(link.x1, link.y1, link.h1, link.x2, link.y2, link.h2)}
              fill={hi ? 'url(#sankey-grad-hi)' : 'url(#sankey-grad)'}
              opacity={dimmed ? 0.06 : 1}
              stroke={hi ? READINESS_COLOR : 'none'}
              strokeWidth={hi ? 0.5 : 0}
              style={{ transition: 'opacity 0.2s, fill 0.2s', cursor: 'pointer' }}
              onMouseEnter={(e) => {
                setHovered({ type: 'link', key: link.key });
                setTip(`Readiness ${link.readiness} → Use ${link.use}: ${link.count} innovation${link.count !== 1 ? 's' : ''}`);
                setTipPos({ x: e.clientX, y: e.clientY });
              }}
              onMouseMove={handleMouseMove}
              onMouseLeave={() => { setHovered(null); setTip(''); }}
            />
          );
        })}

        {/* Readiness nodes (left) — all 9 levels */}
        {ALL_LEVELS.map(l => {
          const n = rNodes[l];
          const hasFlow = n.total > 0;
          const hi = hovered?.type === 'rNode' && hovered.level === l;
          const dimmed = isAnythingHovered && !hi && !(hovered?.type === 'link' && links.some(lk => lk.key === hovered.key && lk.readiness === l));
          return (
            <g key={`r-${l}`}
              onMouseEnter={(e) => {
                setHovered({ type: 'rNode', level: l });
                setTip(`Readiness Level ${l}: ${n.total} innovation${n.total !== 1 ? 's' : ''}`);
                setTipPos({ x: e.clientX, y: e.clientY });
              }}
              onMouseMove={handleMouseMove}
              onMouseLeave={() => { setHovered(null); setTip(''); }}
              style={{ cursor: 'pointer' }}
            >
              <rect
                x={n.x} y={n.y} width={NODE_W} height={n.h}
                fill={hasFlow ? READINESS_COLOR : '#e5e7eb'}
                opacity={dimmed ? 0.3 : 1}
                rx={3}
                style={{ transition: 'opacity 0.2s' }}
              />
              <text
                x={n.x - 6} y={n.y + n.h / 2}
                textAnchor="end" dominantBaseline="central"
                fontSize={11} fontWeight={500} fill={dimmed ? '#d1d5db' : hasFlow ? '#374151' : '#9ca3af'}
                style={{ transition: 'fill 0.2s' }}
              >
                Level {l}
              </text>
              {hasFlow && n.h >= 12 && (
                <text
                  x={n.x + NODE_W / 2} y={n.y + n.h / 2}
                  textAnchor="middle" dominantBaseline="central"
                  fontSize={9} fontWeight={700} fill="#fff"
                >
                  {n.total}
                </text>
              )}
            </g>
          );
        })}

        {/* Use nodes (right) — all 9 levels */}
        {ALL_LEVELS.map(l => {
          const n = uNodes[l];
          const hasFlow = n.total > 0;
          const hi = hovered?.type === 'uNode' && hovered.level === l;
          const dimmed = isAnythingHovered && !hi && !(hovered?.type === 'link' && links.some(lk => lk.key === hovered.key && lk.use === l));
          return (
            <g key={`u-${l}`}
              onMouseEnter={(e) => {
                setHovered({ type: 'uNode', level: l });
                setTip(`Use Level ${l}: ${n.total} innovation${n.total !== 1 ? 's' : ''}`);
                setTipPos({ x: e.clientX, y: e.clientY });
              }}
              onMouseMove={handleMouseMove}
              onMouseLeave={() => { setHovered(null); setTip(''); }}
              style={{ cursor: 'pointer' }}
            >
              <rect
                x={n.x} y={n.y} width={NODE_W} height={n.h}
                fill={hasFlow ? USE_COLOR : '#e5e7eb'}
                opacity={dimmed ? 0.3 : 1}
                rx={3}
                style={{ transition: 'opacity 0.2s' }}
              />
              <text
                x={n.x + NODE_W + 6} y={n.y + n.h / 2}
                textAnchor="start" dominantBaseline="central"
                fontSize={11} fontWeight={500} fill={dimmed ? '#d1d5db' : hasFlow ? '#374151' : '#9ca3af'}
                style={{ transition: 'fill 0.2s' }}
              >
                Level {l}
              </text>
              {hasFlow && n.h >= 12 && (
                <text
                  x={n.x + NODE_W / 2} y={n.y + n.h / 2}
                  textAnchor="middle" dominantBaseline="central"
                  fontSize={9} fontWeight={700} fill="#fff"
                >
                  {n.total}
                </text>
              )}
            </g>
          );
        })}
      </svg>

      {/* Tooltip */}
      {tip && (
        <div
          className="fixed z-50 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg pointer-events-none"
          style={{ left: tipPos.x + 14, top: tipPos.y - 32 }}
        >
          {tip}
        </div>
      )}
    </div>
  );
}

/* ── Links detail modal ────────────────────────────────────────────────── */

const LINKS_PER_PAGE_OPTIONS = [10, 25, 50];

function LinksDetailModal({ links, devCount, onClose }) {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const handleKeyDown = useCallback((e) => { if (e.key === 'Escape') onClose(); }, [onClose]);
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', handleKeyDown); document.body.style.overflow = ''; };
  }, [handleKeyDown]);

  const filtered = useMemo(() => {
    if (!search.trim()) return links;
    const q = search.toLowerCase();
    return links.filter(l =>
      String(l.devCode).toLowerCase().includes(q) ||
      (l.devTitle || '').toLowerCase().includes(q) ||
      String(l.useCode).toLowerCase().includes(q) ||
      (l.useTitle || '').toLowerCase().includes(q)
    );
  }, [links, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const pageRows = filtered.slice((page - 1) * perPage, page * perPage);

  useEffect(() => { setPage(1); }, [search, perPage]);

  const isFiltered = search.trim();
  const countLabel = isFiltered ? `${filtered.length} of ${links.length}` : `${links.length}`;

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-10 flex flex-col w-full max-w-5xl max-h-[80vh] bg-[var(--surface-primary)] rounded-xl shadow-2xl overflow-hidden mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-[var(--border-primary)]">
          <div>
            <h3 className="text-sm font-semibold text-[var(--text-primary)]">Development → Use Links ({countLabel})</h3>
            <p className="text-xs text-[var(--text-tertiary)]">{devCount} development result{devCount !== 1 ? 's' : ''} with use links</p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-[var(--surface-hover)] text-[var(--text-tertiary)]">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search */}
        <div className="px-5 py-3 border-b border-[var(--border-primary)]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-tertiary)]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by result code, title, or reason..."
              className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-[var(--border-primary)] bg-[var(--surface-primary)] text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:ring-2 focus:ring-blue-500/30"
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-y-auto flex-1">
          <table className="w-full text-sm table-fixed">
            <colgroup>
              <col className="w-[10%]" />
              <col className="w-[30%]" />
              <col className="w-[10%]" />
              <col className="w-[10%]" />
              <col className="w-[30%]" />
              <col className="w-[10%]" />
            </colgroup>
            <thead className="sticky top-0 bg-[var(--surface-secondary)] text-left">
              <tr>
                <th className="px-4 py-2 font-medium text-[var(--text-secondary)]">Dev Code</th>
                <th className="px-4 py-2 font-medium text-[var(--text-secondary)]">Dev Title</th>
                <th className="px-4 py-2 font-medium text-[var(--text-secondary)] text-center">Readiness</th>
                <th className="px-4 py-2 font-medium text-[var(--text-secondary)]">Use Code</th>
                <th className="px-4 py-2 font-medium text-[var(--text-secondary)]">Use Title</th>
                <th className="px-4 py-2 font-medium text-[var(--text-secondary)] text-center">Use Level</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border-primary)]">
              {pageRows.map((link, i) => (
                <tr key={i}>
                  <td className="px-4 py-2 text-[var(--text-primary)] font-mono text-xs">{link.devCode || '—'}</td>
                  <td className="px-4 py-2 text-[var(--text-primary)] break-words">{link.devTitle || '—'}</td>
                  <td className="px-4 py-2 text-center">
                    <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-700">{link.readiness}</span>
                  </td>
                  <td className="px-4 py-2 text-[var(--text-primary)] font-mono text-xs">{link.useCode || '—'}</td>
                  <td className="px-4 py-2 text-[var(--text-primary)] break-words">{link.useTitle || '—'}</td>
                  <td className="px-4 py-2 text-center">
                    <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">{link.useLevel}</span>
                  </td>
                </tr>
              ))}
              {pageRows.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-[var(--text-tertiary)]">No results match your search.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-[var(--border-primary)] text-xs text-[var(--text-secondary)]">
          <div className="flex items-center gap-2">
            <span>Rows per page:</span>
            <select
              value={perPage}
              onChange={(e) => setPerPage(Number(e.target.value))}
              className="px-2 py-1 rounded border border-[var(--border-primary)] bg-[var(--surface-primary)] text-[var(--text-primary)] text-xs focus:outline-none"
            >
              {LINKS_PER_PAGE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-3">
            <span>Page {page} of {totalPages}</span>
            <button
              onClick={() => setPage(p => p - 1)}
              disabled={page <= 1}
              className="p-1 rounded hover:bg-[var(--surface-hover)] disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={page >= totalPages}
              className="p-1 rounded hover:bg-[var(--surface-hover)] disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/* ── Main chart ────────────────────────────────────────────────────────── */

export const InnovationEvolutionChart = memo(function InnovationEvolutionChart() {
  const { devToUseEvolution, totalCount, innovationResults } = useInnovationData();
  if (!devToUseEvolution || devToUseEvolution.totalDev === 0) return (
    <ChartWrapper title="Innovation Development → Use Evolution">
      <NoChartData message="No Innovation Development results to track" detail="This chart tracks the Development-to-Use pipeline by analyzing linked results between Innovation Development and Innovation Use entries. The current selection has no Innovation Development results." />
    </ChartWrapper>
  );

  const [showLinksModal, setShowLinksModal] = useState(false);
  const { totalDev, totalUse, devWithUseLinks, useWithDevLinks, conversionRate, devWithUseLevels, devWithoutUseLevels, sankeyFlows, detailedLinks, totalDevUseLinks } = devToUseEvolution;

  const levels = [1, 2, 3, 4, 5, 6, 7, 8, 9];
  const levelData = levels.map(l => ({
    level: `Level ${l}`,
    'With Use link': devWithUseLevels[l] || 0,
    'Without Use link': devWithoutUseLevels[l] || 0,
  }));

  const totalPairs = sankeyFlows ? sankeyFlows.reduce((s, f) => s + f.count, 0) : 0;

  const exportData = [
    { Metric: 'Total Innovation Development results', Value: totalDev },
    { Metric: 'Total Innovation Use results', Value: totalUse },
    { Metric: 'Dev results linked to Use', Value: devWithUseLinks },
    { Metric: 'Use results linked back to Dev', Value: useWithDevLinks },
    { Metric: 'Dev→Use conversion rate (%)', Value: conversionRate },
    ...levels.map(l => ({
      Metric: `Readiness Level ${l} (with Use link)`, Value: devWithUseLevels[l] || 0,
    })),
    ...levels.map(l => ({
      Metric: `Readiness Level ${l} (without Use link)`, Value: devWithoutUseLevels[l] || 0,
    })),
  ];

  return (
    <ChartWrapper title="Innovation Development → Use Evolution" info="Tracks which innovation development results have corresponding use results, showing the development-to-adoption conversion pipeline." chartData={exportData} exportFilename="innovation-dev-use-evolution" sourceResults={innovationResults}>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <div className="bg-purple-50 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-purple-700">{totalDev}</p>
          <p className="text-xs text-purple-600">Innovation Development Results</p>
        </div>
        <div className="bg-green-50 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-green-700">{totalUse}</p>
          <p className="text-xs text-green-600">Innovation Use Results</p>
        </div>
        <div className="bg-blue-50 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-blue-700">{devWithUseLinks}</p>
          <p className="text-xs text-blue-600">Dev Results with Use Links</p>
        </div>
        <div className="bg-amber-50 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-amber-700">{conversionRate}%</p>
          <p className="text-xs text-amber-600">Linkage Rate</p>
        </div>
      </div>

      <h5 className="text-sm font-medium text-gray-700 mb-2">
        Readiness Level: Development Innovations With vs Without Use Links
      </h5>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={levelData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="level" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 12 }} />
          <Tooltip contentStyle={getTooltipStyle()} />
          <Legend />
          <Bar dataKey="With Use link" fill="#8b5cf6" radius={[4, 4, 0, 0]}>
            <LabelList dataKey="With Use link" position="top" style={LABEL_STYLE} />
          </Bar>
          <Bar dataKey="Without Use link" fill="#d1d5db" radius={[4, 4, 0, 0]}>
            <LabelList dataKey="Without Use link" position="top" style={LABEL_STYLE} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <p className="text-xs text-gray-400 text-center mt-1">
        Comparing Readiness Levels of Development Innovations; those with links to Innovation Use vs those without links
      </p>

      {sankeyFlows && sankeyFlows.length > 0 && (
        <div className="mt-6">
          <div className="flex items-center justify-between mb-1">
            <h5 className="text-base font-semibold text-gray-700">
              Readiness Level → Use Level
            </h5>
            {detailedLinks && detailedLinks.length > 0 && (
              <button
                onClick={() => setShowLinksModal(true)}
                className="text-sm text-indigo-600 hover:text-indigo-800 font-medium flex items-center gap-1"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
                See Links
              </button>
            )}
          </div>
          <p className="text-sm text-gray-500 mb-1">
            {devWithUseLinks} development result{devWithUseLinks !== 1 ? 's' : ''} link to use results ({totalDevUseLinks} link{totalDevUseLinks !== 1 ? 's' : ''} total)
          </p>
          <p className="text-xs text-gray-400 mb-3">
            Showing {totalPairs} link{totalPairs !== 1 ? 's' : ''} where both the dev readiness level and use level are known
          </p>
          <DevUseSankey flows={sankeyFlows} />
        </div>
      )}

      {showLinksModal && detailedLinks && (
        <LinksDetailModal links={detailedLinks} devCount={devWithUseLinks} onClose={() => setShowLinksModal(false)} />
      )}
    </ChartWrapper>
  );
});
